import { motion, useReducedMotion } from 'framer-motion'
import { Bell, CalendarPlus, ChartNoAxesColumn, Gift, Languages, MapPin, Send, Sparkles } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'

// Pastel icon-chip gradients from the mockup, cycled across the grid.
const CHIP_BG = [
  'linear-gradient(135deg, #ffe0ec, #ffd0c2)',
  'linear-gradient(135deg, #e9defb, #d7ecff)',
  'linear-gradient(135deg, #fdf0c8, #d6f7e8)',
]
const CHIP_TEXT = ['text-celebration-pink', 'text-celebration-grape', 'text-celebration-coral']

// Bubble-tile bento grid of the core product features. Cards vary in span
// (first card is double-wide on desktop) and lift with a springy bounce.
const FeatureBento = () => {
  const { language } = useLanguage()
  const isKa = language === 'ka'
  const reduce = useReducedMotion()

  const reveal = (i: number) => ({
    initial: reduce ? false : { opacity: 0, y: 46, scale: 0.96 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    viewport: { once: true, margin: '-60px' },
    transition: { type: 'spring' as const, stiffness: 220, damping: 18, delay: (i % 3) * 0.12 },
  })

  const cardClass = 'group tile rounded-bubble p-6'

  const iconClass =
    'flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-300 ease-spring group-hover:-rotate-6 group-hover:scale-110'

  const cards = [
    {
      span: 'sm:col-span-2',
      Icon: Sparkles,
      title: isKa ? 'დიზაინი და თემები' : 'Design & themes',
      body: isKa
        ? 'აირჩიე მზა ნიმუშებიდან ან ჩვენ დაგეხმარებით სასურველი დიზაინის შექმნაში.'
        : 'Choose from ready-made designs, or let us help you create the one you want.',
      accent: (
        <div className="mt-5 flex items-center gap-3" aria-hidden="true">
          <span className="landing-shimmer h-2.5 flex-1 rounded-full bg-gradient-to-r from-[#ff4d8d]/30 via-[#8b5cf6]/30 to-[#38bdf8]/30" />
          <span className="rounded-full bg-celebration-violet/10 px-2.5 py-1 text-[11px] font-semibold text-celebration-grape dark:bg-celebration-violet/20 dark:text-[#C4B5FD]">
            {isKa ? 'იქმნება…' : 'Generating…'}
          </span>
        </div>
      ),
    },
    {
      span: '',
      Icon: ChartNoAxesColumn,
      title: isKa ? 'სტუმრების მართვა' : 'Guest management',
      body: isKa
        ? 'სტუმრები გიდასტურებენ დასწრებას, თქვენ კი ხედავთ შედეგს ონლაინ რეჟიმში.'
        : 'Guests confirm their attendance — you watch the results come in live.',
      accent: (
        <div className="mt-5 flex h-10 items-end gap-1.5" aria-hidden="true">
          {[40, 65, 50, 80, 100].map((h, i) => (
            <span
              key={i}
              className="w-3 origin-bottom scale-y-50 rounded-t-sm bg-celebration-pink/60 transition-transform duration-500 group-hover:scale-y-100"
              style={{ height: `${h}%`, transitionDelay: `${i * 60}ms` }}
            />
          ))}
        </div>
      ),
    },
    {
      span: '',
      Icon: Gift,
      title: isKa ? 'საჩუქრების სია + შენატანები' : 'Gift list + contributions',
      body: isKa
        ? 'გაუადვილეთ საჩუქრის არჩევა – შეადგინეთ სურვილების სია და მოუხსენით სტუმრებს საჩუქარზე ფიქრის ტვირთი.'
        : 'Make gift-picking easy — build a wishlist and lift the guesswork off your guests.',
    },
    {
      span: '',
      Icon: MapPin,
      title: isKa ? 'ლოკაცია' : 'Location',
      body: isKa
        ? 'არანაირი დაბნეულობა — მიუთითე ზუსტი ლოკაცია და საჭიროების შემთხვევაში დაამატე ინსტრუქცია, რომ სტუმრებმა მარტივად მოგაგნონ.'
        : 'No confusion — pin the exact location and add directions if they’re needed, so guests find you easily.',
    },
    {
      span: '',
      Icon: Bell,
      title: isKa ? 'შეხსენებები' : 'Reminders',
      body: isKa
        ? 'პერიოდული შეხსენებები სტუმრებისთვის, რომ არ გამოგრჩეთ ღონისძიება.'
        : 'Periodic reminders for guests, so nobody misses the event.',
    },
    {
      span: '',
      Icon: Send,
      title: isKa ? 'მასობრივი დაპატიჟება' : 'Mass invites',
      body: isKa
        ? 'ასობით სტუმარს პირადი ბმული ერთ წუთში გაუგზავნე — ნებისმიერ არხზე.'
        : 'Send hundreds of personal links in a minute — through any channel.',
    },
    {
      span: '',
      Icon: CalendarPlus,
      title: isKa ? 'კალენდარში დამატება' : 'Add to calendar',
      body: isKa
        ? 'ერთი შეხება და ღონისძიება სტუმრის კალენდარშია — აღარავის დაავიწყდება.'
        : 'One tap and the event is on your guest’s calendar — no one forgets.',
    },
    {
      span: '',
      Icon: Languages,
      title: isKa ? 'ორენოვანი მოსაწვევები' : 'Bilingual invites',
      body: isKa
        ? 'ქართული და ინგლისური — თითოეული სტუმარი თავის ენაზე კითხულობს.'
        : 'Georgian and English — every guest reads it in their own language.',
    },
  ]

  return (
    <section className="relative mx-auto max-w-6xl px-5 py-16 lg:py-24">
      {/* Candy blobs drifting behind the grid */}
      <div
        aria-hidden="true"
        className="blob blob-violet blob-drift-slow"
        style={{ width: 420, height: 420, top: 40, right: -200, opacity: 0.22 }}
      />
      <div
        aria-hidden="true"
        className="blob blob-sky blob-drift"
        style={{ width: 340, height: 340, bottom: -80, left: -180, opacity: 0.2 }}
      />
      <div className="relative max-w-[48ch]">
        <h2 className="font-lynx text-[clamp(1.9rem,3.5vw,2.75rem)] font-semibold tracking-tight">
          {isKa ? 'მარტივად გააზიარე შენი ღონისძიების დეტალები' : 'Share your event details with ease'}
        </h2>
      </div>
      <div className="relative mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ span, Icon, title, body, accent }, i) => (
          <motion.div key={title} {...reveal(i)} className={`${cardClass} ${span}`}>
            <span className={`${iconClass} ${CHIP_TEXT[i % 3]}`} style={{ background: CHIP_BG[i % 3] }}>
              <Icon className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-celebration-ink-2 dark:text-[#c3b8dd]">
              {body}
            </p>
            {accent}
          </motion.div>
        ))}
      </div>
    </section>
  )
}

export default FeatureBento
